import { ChangeEvent, useEffect, useMemo } from 'react';

import { randomStr } from '../../../utils/helpers';
import { useRadioGroupContext } from './Context';

export interface UseRadioGroup {
  /**
   * canUncheck only has an impact when user clicks on already checked radio option.
   *
   * when `canUncheck === true` then clicking on checked radio option will uncheck it.
   * `else` clicking on checked radio option will not have any impact
   *
   * @default false
   */
  canUncheck?: boolean;
  value?: string | null;
  onChange?: (value: string | null) => void;
}

const useRadioGroup = ({ canUncheck = false, value = null, onChange }: UseRadioGroup) => {
  const labelId = useMemo(() => `radio-group-label-${randomStr(4)}`, []);

  const {
    setCanUncheck,
    value: contextValue,
    setValue: setContextValue,
    setEmitChange,
  } = useRadioGroupContext();

  // when the group receives an onChange prop from consumer, publish it into context
  useEffect(() => {
    setEmitChange(() => onChange);
    // cleanup to avoid stale functions when unmounting or changing handlers
    return () => setEmitChange(undefined);
  }, [onChange, setEmitChange]);

  // No longer rely on group onChange for notifying consumer; RadioOption will call emitChange
  // But keep a lightweight handler if you want to expose the native signature — here we do nothing
  const handleChange = (_event: ChangeEvent<HTMLInputElement>, _value: string) => {
    // Intentionally empty — we let RadioOption drive context and call emitChange
  };

  useEffect(
    function setInitialCanUncheck() {
      setCanUncheck(canUncheck);
    },
    [canUncheck, setCanUncheck]
  );

  useEffect(
    function setInitialValue() {
      setContextValue(value);
    },
    [value, setContextValue]
  );

  return { labelId, contextValue, handleChange };
};

export default useRadioGroup;
